/**
 * Finds the nearest scrollable ancestor with enough overflow to benefit from
 * programmatic scrolling.
 */
export function findScrollableParent(
  element: Element | null,
  options?: { minScrollBuffer?: number }
): HTMLElement | null {
  const minBuffer = options?.minScrollBuffer ?? 100;
  let current =
    element instanceof HTMLElement ? element : element?.parentElement;

  while (current && current !== document.body) {
    const style = getComputedStyle(current);
    if (
      (style.overflowY === "auto" || style.overflowY === "scroll") &&
      current.scrollHeight > current.clientHeight + minBuffer
    ) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
}

/**
 * Centers the selected text range rather than its containing element, which
 * keeps matches in large elements such as code blocks correctly positioned.
 */
export function scrollRangeToCenter(
  range: Range,
  options?: { behavior?: ScrollBehavior; fallbackToScrollIntoView?: boolean }
): void {
  const { behavior = "auto", fallbackToScrollIntoView = true } = options ?? {};

  const rects = range.getClientRects();
  if (rects.length === 0) return;

  const selectionRect = rects[0];
  if (selectionRect === undefined) return;
  const scrollableParent = findScrollableParent(
    range.startContainer.parentElement
  );

  if (scrollableParent) {
    const parentRect = scrollableParent.getBoundingClientRect();
    const selectionOffsetInParent =
      selectionRect.top - parentRect.top + scrollableParent.scrollTop;
    const targetScrollTop =
      selectionOffsetInParent - scrollableParent.clientHeight / 2;

    scrollableParent.scrollTo({
      top: Math.max(0, targetScrollTop),
      behavior,
    });
  } else if (fallbackToScrollIntoView) {
    range.startContainer.parentElement?.scrollIntoView({
      behavior,
      block: "center",
    });
  }
}
